import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RetentionStatus } from '@/application/metrics';
import {
  matchesProfessionalNameSearch,
  type OwnerProfessionalRankingRow,
} from '@/application/metrics';
import { getOwnerProfessionalsRanking } from '@/infrastructure/http/metricsApi';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { Breadcrumbs } from '@/presentation/components/common/Breadcrumbs';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { DomainScopeBar } from '@/presentation/components/domain/DomainScopeBar';
import { formatCurrency, formatInteger } from '@/presentation/components/metrics/metricFormatters';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { formatSigned } from '@/presentation/utils/metricDeltas';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { persistReportScope } from '@/application/metrics/metricsScope';
import {
  matchesOwnerStatusFilter,
  presentRetentionStatus,
  type OwnerStatusFilter,
} from '@/application/metrics/pvpRetentionPresentation';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import {
  ResponsiveDataList,
  type ResponsiveColumnDef,
} from '@/presentation/components/common/ResponsiveDataList';
import { OwnerStatusBadge } from '@/presentation/components/metrics/OwnerStatusBadge';

type SortKey = 'name' | 'clinic' | 'pvp' | 'vsMonth' | 'vsHist' | 'patients' | 'revenue' | 'status';

function statusRank(status: RetentionStatus): number {
  if (status === 'critical') return 0;
  if (status === 'alert') return 1;
  if (status === 'observation') return 2;
  if (status === 'insufficient_history') return 3;
  return 4;
}

function vsMonthValue(row: OwnerProfessionalRankingRow): number | null {
  return row.pvpVariacionPct ?? row.deltaIngresosPct;
}

function vsHistValue(row: OwnerProfessionalRankingRow): number | null {
  if (row.pvp === null || row.pvpPromedioHistorico === null || row.pvpPromedioHistorico === 0) {
    return null;
  }
  return ((row.pvp - row.pvpPromedioHistorico) / row.pvpPromedioHistorico) * 100;
}

/** nulls siempre al final. */
function compareNullable(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

function formatPctOrDash(locale: string, value: number | null): string {
  return value === null ? '—' : `${formatSigned(locale, value)}%`;
}

function formatPvpOrDash(locale: string, value: number | null): string {
  return value === null ? '—' : value.toLocaleString(locale, { maximumFractionDigits: 1 });
}

function detailHref(row: OwnerProfessionalRankingRow, period: string): string {
  return `/professionals/${row.profesionalId}?tenant=${row.tenantId}&period=${period}`;
}

/** Ranking transversal de profesionales: tabla desktop, cards mobile (#40). */
export function ProfessionalsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const { period, year, month, label: periodLabel } = useGlobalPeriod();
  const locale = i18n.language;
  const [searchParams] = useSearchParams();
  const tenantFromUrl = searchParams.get('tenant') ?? '';

  const tenantIds = useMemo(
    () => user?.scope.allowedTenantIds ?? [],
    [user?.scope.allowedTenantIds]
  );
  const isMulti = tenantIds.length > 1;
  const [clinicFilter, setClinicFilter] = useState<string>(() => {
    if (tenantFromUrl && tenantIds.includes(tenantFromUrl)) return tenantFromUrl;
    return 'all';
  });
  const [statusFilter, setStatusFilter] = useState<OwnerStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDesc, setSortDesc] = useState(false);
  const [rows, setRows] = useState<OwnerProfessionalRankingRow[]>([]);
  const [totals, setTotals] = useState({ profesionales: 0, enAtencion: 0, enCaida: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canView = hasPermission('metrics.profesionales');
  const clinicOptions = useMemo(
    () => [
      { value: 'all', label: t('domain.professionals.allClinics') },
      ...tenantIds.map((id) => ({ value: id, label: clinicNames[id] ?? id })),
    ],
    [tenantIds, clinicNames, t]
  );

  useEffect(() => {
    if (tenantFromUrl && tenantIds.includes(tenantFromUrl) && clinicFilter !== tenantFromUrl) {
      setClinicFilter(tenantFromUrl);
    }
  }, [tenantFromUrl, tenantIds, clinicFilter]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !canView || tenantIds.length === 0) {
      setRows([]);
      return;
    }

    void (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const ranking = await getOwnerProfessionalsRanking(token, {
          year,
          month,
          tenantId: clinicFilter === 'all' ? undefined : clinicFilter,
          months: 6,
          limit: 300,
        });
        if (cancelled) return;
        setRows(ranking.rows);
        setTotals(ranking.totals);
      } catch (err) {
        if (!cancelled) {
          setError(t('metrics.states.error.message'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, canView, tenantIds.length, clinicFilter, year, month, t]);

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter !== 'all') {
      list = list.filter((r) => matchesOwnerStatusFilter(r.status, statusFilter));
    }
    if (search.trim()) {
      list = list.filter((r) => matchesProfessionalNameSearch(r, search));
    }

    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = (a.nombre || `#${a.profesionalId}`).localeCompare(
          b.nombre || `#${b.profesionalId}`,
          locale
        );
      } else if (sortKey === 'clinic') {
        const aName = clinicNames[a.tenantId] ?? a.tenantId;
        const bName = clinicNames[b.tenantId] ?? b.tenantId;
        cmp = aName.localeCompare(bName, locale);
      } else if (sortKey === 'pvp') {
        cmp = compareNullable(a.pvp, b.pvp);
      } else if (sortKey === 'vsMonth') {
        cmp = compareNullable(vsMonthValue(a), vsMonthValue(b));
      } else if (sortKey === 'vsHist') {
        cmp = compareNullable(vsHistValue(a), vsHistValue(b));
      } else if (sortKey === 'patients') {
        cmp = a.turnosAtendidos - b.turnosAtendidos;
      } else if (sortKey === 'revenue') {
        cmp = a.ingresos - b.ingresos;
      } else {
        cmp = statusRank(a.status) - statusRank(b.status);
      }

      if (sortDesc) cmp = -cmp;
      if (cmp === 0) cmp = statusRank(a.status) - statusRank(b.status);
      if (cmp === 0) cmp = b.ingresos - a.ingresos;
      return cmp;
    });

    return sorted;
  }, [rows, statusFilter, search, sortKey, sortDesc, clinicNames, locale]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc((v) => !v);
    } else {
      setSortKey(key);
      setSortDesc(key !== 'name' && key !== 'clinic' && key !== 'status');
    }
  };

  const sortMark = (key: SortKey) => (sortKey === key ? (sortDesc ? ' ↓' : ' ↑') : '');

  const openProfessional = (row: OwnerProfessionalRankingRow) => {
    persistReportScope(period, row.tenantId);
    navigate(detailHref(row, period));
  };

  const displayName = (row: OwnerProfessionalRankingRow) =>
    row.nombre || `#${row.profesionalId}`;

  const clinicLabel = (row: OwnerProfessionalRankingRow) =>
    clinicNames[row.tenantId] ?? row.tenantId;

  const nameLink = (row: OwnerProfessionalRankingRow) => (
    <Link
      to={detailHref(row, period)}
      className="font-semibold text-gray-900 hover:text-teal-700 dark:text-white dark:hover:text-teal-300"
      onClick={() => persistReportScope(period, row.tenantId)}
    >
      {displayName(row)}
    </Link>
  );

  const trendDownBadge = (row: OwnerProfessionalRankingRow) =>
    row.trend === 'down' ? (
      <span
        data-testid="professionals-trend-down"
        className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
      >
        {t('domain.professionals.trendDown')}
      </span>
    ) : null;

  const statusCell = (row: OwnerProfessionalRankingRow, layout: 'row' | 'wrap' = 'wrap') => {
    const presentation = presentRetentionStatus(row.status);
    return (
      <div
        className={
          layout === 'row'
            ? 'inline-flex items-center gap-1.5 whitespace-nowrap'
            : 'flex flex-wrap items-center gap-1.5'
        }
      >
        <OwnerStatusBadge
          label={t(presentation.labelKey)}
          tip={t(presentation.tipKey)}
          tone={presentation.tone}
          testId={`professionals-status-${row.status}`}
        />
        {trendDownBadge(row)}
      </div>
    );
  };

  const columns: ResponsiveColumnDef<OwnerProfessionalRankingRow>[] = [
    {
      key: 'name',
      sortable: true,
      sortLabel: t('domain.professionals.col.name'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('name')}
        >
          {t('domain.professionals.col.name')}
          {sortMark('name')}
        </button>
      ),
      width: isMulti ? '16%' : '22%',
      cell: (row) => nameLink(row),
    },
    ...(isMulti
      ? [
          {
            key: 'clinic',
            sortable: true,
            sortLabel: t('domain.professionals.col.clinic'),
            header: (
              <button
                type="button"
                className="hover:text-teal-700 dark:hover:text-teal-300"
                onClick={() => toggleSort('clinic')}
              >
                {t('domain.professionals.col.clinic')}
                {sortMark('clinic')}
              </button>
            ),
            width: '14%',
            cell: (row: OwnerProfessionalRankingRow) => clinicLabel(row),
            cellClassName: 'text-gray-700 dark:text-gray-300',
          } satisfies ResponsiveColumnDef<OwnerProfessionalRankingRow>,
        ]
      : []),
    {
      key: 'pvp',
      sortable: true,
      sortLabel: t('domain.professionals.col.pvp'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('pvp')}
        >
          {t('domain.professionals.col.pvp')}
          {sortMark('pvp')}
        </button>
      ),
      width: '7%',
      cell: (row) => formatPvpOrDash(locale, row.pvp),
      cellClassName: 'tabular-nums',
    },
    {
      key: 'vsMonth',
      sortable: true,
      sortLabel: t('domain.professionals.col.vsMonth'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('vsMonth')}
        >
          {t('domain.professionals.col.vsMonth')}
          {sortMark('vsMonth')}
        </button>
      ),
      width: '10%',
      cell: (row) => formatPctOrDash(locale, vsMonthValue(row)),
      cellClassName: 'tabular-nums',
    },
    {
      key: 'vsHist',
      sortable: true,
      sortLabel: t('domain.professionals.col.vsHist'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('vsHist')}
        >
          {t('domain.professionals.col.vsHist')}
          {sortMark('vsHist')}
        </button>
      ),
      width: '10%',
      cell: (row) => formatPctOrDash(locale, vsHistValue(row)),
      cellClassName: 'tabular-nums',
    },
    {
      key: 'patients',
      sortable: true,
      sortLabel: t('domain.professionals.col.patients'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('patients')}
        >
          {t('domain.professionals.col.patients')}
          {sortMark('patients')}
        </button>
      ),
      width: '8%',
      cell: (row) => formatInteger(row.turnosAtendidos, locale),
      cellClassName: 'tabular-nums',
    },
    {
      key: 'revenue',
      sortable: true,
      sortLabel: t('domain.professionals.col.revenue'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('revenue')}
        >
          {t('domain.professionals.col.revenue')}
          {sortMark('revenue')}
        </button>
      ),
      width: '13%',
      cell: (row) => formatCurrency(row.ingresos, locale),
      cellClassName: 'tabular-nums',
    },
    {
      key: 'status',
      sortable: true,
      sortLabel: t('domain.professionals.col.status'),
      header: (
        <button
          type="button"
          className="hover:text-teal-700 dark:hover:text-teal-300"
          onClick={() => toggleSort('status')}
        >
          {t('domain.professionals.col.status')}
          {sortMark('status')}
        </button>
      ),
      width: '18%',
      headerClassName: 'whitespace-nowrap pr-5',
      cellClassName: 'whitespace-nowrap pr-5',
      cell: (row) => statusCell(row, 'row'),
    },
  ];

  const emptyMessage =
    rows.length === 0
      ? t('domain.professionals.empty')
      : t('domain.professionals.emptyFiltered');

  if (!canView) {
    return <AccessDenied domain="professionals" homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  return (
    <div className="panel-page panel-page--fixed panel-safe-bottom flex h-full min-h-0 flex-col gap-4 !space-y-0">
      <div className="shrink-0 space-y-4">
        <Breadcrumbs
          label={t('breadcrumbs.nav')}
          items={[{ label: t('breadcrumbs.professionals') }]}
        />
        <DomainPageHeader
          title={t('domain.professionals.title')}
          subtitle={t('domain.professionals.subtitle', {
            period: periodLabel,
          })}
        />

        <DomainScopeBar
          showClinic={isMulti}
          clinicId={clinicFilter}
          clinicOptions={clinicOptions}
          onClinicChange={(value) => {
            setClinicFilter(value);
            if (value !== 'all') persistReportScope(period, value);
          }}
          actions={
            <>
              <label className="min-w-0 sm:max-w-xs">
                <span className="sr-only">{t('domain.professionals.search')}</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('domain.professionals.searchPlaceholder')}
                  data-testid="professionals-search"
                  className="h-9 w-full min-w-[12rem] rounded-lg border border-slate-200 bg-white px-2.5 text-sm dark:border-gray-700 dark:bg-gray-900"
                />
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
              >
                <option value="all">{t('domain.professionals.filterAll')}</option>
                <option value="critical">{t('domain.professionals.filterCritical')}</option>
                <option value="attention">{t('domain.professionals.filterAttention')}</option>
                <option value="ok">{t('domain.professionals.filterOk')}</option>
              </select>
            </>
          }
        />

        {!isLoading && !error && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('domain.professionals.totalsLine', {
              total: totals.profesionales,
              attention: totals.enAtencion,
              down: totals.enCaida,
            })}
          </p>
        )}

        {error && (
          <PanelPageState variant="error" layout="inline" onRetry={() => window.location.reload()} />
        )}
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="professionals-ranking-table"
      >
        <ResponsiveDataList
          className="flex min-h-0 flex-1 flex-col"
          pinHeader
          rows={filtered}
          keyField={(row) => `${row.tenantId}-${row.profesionalId}`}
          columns={columns}
          loading={isLoading}
          loadingSlot={
            <div className="rounded-xl border border-[var(--bi-line)] bg-white/80 dark:bg-white/5">
              <LoadingBlock minHeightClassName="min-h-[12rem]" />
            </div>
          }
          emptyMessage={emptyMessage}
          emptySlot={
            <p data-testid="professionals-empty">{emptyMessage}</p>
          }
          ariaLabel={t('domain.professionals.title')}
          onRowClick={openProfessional}
          sort={{ key: sortKey, direction: sortDesc ? 'desc' : 'asc' }}
          onSortChange={({ key, direction }) => {
            const next = key as SortKey;
            if (next !== sortKey) {
              setSortKey(next);
              setSortDesc(next !== 'name' && next !== 'clinic' && next !== 'status');
              return;
            }
            setSortDesc(direction === 'desc');
          }}
          renderCard={(row) => (
            <div data-testid={`professionals-mobile-card-${row.tenantId}-${row.profesionalId}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate">{nameLink(row)}</p>
                  {isMulti ? (
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {clinicLabel(row)}
                    </p>
                  ) : null}
                </div>
                {statusCell(row)}
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="min-w-0">
                  <dt className="truncate text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('domain.professionals.col.revenue')}
                  </dt>
                  <dd className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {formatCurrency(row.ingresos, locale)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="truncate text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('domain.professionals.col.patients')}
                  </dt>
                  <dd className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {formatInteger(row.turnosAtendidos, locale)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="truncate text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('domain.professionals.col.pvp')}
                  </dt>
                  <dd className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {formatPvpOrDash(locale, row.pvp)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="truncate text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('domain.professionals.col.vsMonth')}
                  </dt>
                  <dd className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {formatPctOrDash(locale, vsMonthValue(row))}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        />
      </div>
    </div>
  );
}
